/**
 * Billing Module service.
 *
 * Translates camelCase UI payloads → snake_case repository payloads and wraps
 * everything in `IControllerPromiseResponse<T, { message }>` so callers get
 * uniform error handling.
 *
 * `bulkUpdateBillingStatus` is implemented client-side as N parallel
 * `updateBillingStatus` calls — there is no dedicated backend endpoint.
 */

import { fromV2ForAdminExport } from "../../../util/billingV2ToLegacy";

export const billingModuleServiceImplFactory = ({
  billingModuleRepository,
}: {
  billingModuleRepository: IBillingModuleRepository;
}): IBillingModuleService => {
  const toErr = (error: unknown) => ({
    message: (error as Error)?.message ?? "Unknown billing module error",
  });

  /**
   * Monthly billing listing — now sourced from `GET /pending-tasks-v3`
   * (the legacy `/billing/v11/clinic/monthly` endpoint is replaced).
   *
   * The V3 response shape differs from V11 (paginated envelope of
   * `V3PatientRow` instead of `{ data: { list_of_data: BillingRow[] } }`),
   * so each row is adapted back to the legacy `BillingRow` shape via
   * `billingV2ToLegacy.fromV2ForAdminExport` — which also reconstructs the
   * RPM CPT flags from the posted `charges[]` (the V3 payload doesn't carry
   * the V11 eligibility flags). Consumers keep receiving `{ rows }`
   * unchanged.
   *
   * The backend caps `page_size` at 200; we default to that and auto-page on
   * `paging.has_more`. A safety cap (`MAX_PAGES = 25000`) guards against a
   * runaway loop if the backend ever returns `has_more=true` indefinitely.
   *
   * `date` (any day inside the target month — callers pass the last day) is
   * forwarded as `selected_month`; the backend normalizes it to
   * first-of-month.
   */
  const getBillingMonthly: IGetBillingMonthlyService = async ({
    clinicId,
    date,
    patientId,
  }) => {
    const MAX_PAGES = 25000;
    const PAGE_SIZE = 200;
    try {
      const allRows: import("../ports/BillingModule").V3PatientRow[] = [];

      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const response = await billingModuleRepository.getPendingTasksV3({
          selected_month: date,
          clinic_id: clinicId,
          ...(typeof patientId === "number" ? { patient_id: patientId } : {}),
          page,
          page_size: PAGE_SIZE,
        });

        const pageRows = Array.isArray(response?.data) ? response.data : [];
        if (pageRows.length) allRows.push(...pageRows);

        if (!response?.paging?.has_more) break;
      }

      return {
        success: true,
        data: { rows: allRows.map(fromV2ForAdminExport) },
      };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const getBillingPatientMonthly: IGetBillingPatientMonthlyService = async ({
    patientId,
    clinicId,
    date,
  }) => {
    try {
      const response = await billingModuleRepository.getBillingPatientMonthly({
        patient_id: patientId,
        clinic_id: clinicId,
        date,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const getSignOffAssessment: IGetSignOffAssessmentService = async ({
    clinicId,
    patientId,
    billingDate,
  }) => {
    try {
      const response = await billingModuleRepository.getSignOffAssessment({
        clinic_id: clinicId,
        patient_id: patientId,
        billing_date: billingDate,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const updateBillingStatus: IUpdateBillingStatusService = async ({
    clinicId,
    patientId,
    billingDate,
    changes,
  }) => {
    try {
      const response = await billingModuleRepository.updateBillingStatus({
        clinic_id: clinicId,
        patient_id: patientId,
        billing_date: billingDate,
        body: changes,
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  /**
   * Client-side bulk: runs every update in parallel. Each promise is wrapped
   * so a single failure cannot reject the overall result — callers always
   * receive one entry per patient (in input order).
   */
  const bulkUpdateBillingStatus: IBulkUpdateBillingStatusService = async ({
    clinicId,
    billingDate,
    patientIds,
    changes,
  }) => {
    const results = await Promise.all(
      patientIds.map(async (patientId) => {
        const response = await updateBillingStatus({
          clinicId,
          patientId,
          billingDate,
          changes,
        });
        return { patientId, response };
      })
    );
    const failedCount = results.filter((r) => !r.response.success).length;
    return { results, failedCount };
  };

  const updateSignOffAssessment: IUpdateSignOffAssessmentService = async ({
    clinicId,
    patientId,
    billingDate,
    assessment,
  }) => {
    try {
      const response = await billingModuleRepository.updateSignOffAssessment({
        clinic_id: clinicId,
        patient_id: patientId,
        billing_date: billingDate,
        body: assessment,
      });
      return { success: true, data: { raw: response } };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  /**
   * Per-patient per-month billing charges.
   *
   * The service owns the `YYYY-MM` → `{month, year}` split so callers (and
   * the modal hook) work in a single ISO month key rather than splitting it
   * themselves. Falls back to `'01'`/`'1970'` for an invalid input instead
   * of throwing — the API will return an empty list and the modal will show
   * "no charges billed".
   */
  const getBillingChargesByPatientMonth: IGetBillingChargesByPatientMonthService =
    async ({ clinicId, patientId, monthKey }) => {
      try {
        const match = /^(\d{4})-(\d{2})$/.exec(monthKey ?? "");
        const year = match?.[1] ?? "1970";
        const month = match?.[2] ?? "01";

        const response =
          await billingModuleRepository.getBillingChargesByPatientMonth({
            clinic_id: clinicId,
            patient_id: patientId,
            month,
            year,
          });

        return {
          success: true,
          data: {
            charges: Array.isArray(response?.data) ? response.data : [],
            count: typeof response?.count === "number" ? response.count : 0,
          },
        };
      } catch (error) {
        return { success: false, error: toErr(error) };
      }
    };

  /**
   * Chart Review sign-off.
   *
   * Matches the confirmed-working legacy BulkChartReviewModal2 payload:
   *   - `sign_off_information` wrapper with `datetime` + `json_data` inside
   *   - No `file` in the body — the backend generates the PDF server-side
   *     from `json_data`
   *   - `patient_id`, `clinic_id`, `date`, `tracking_time` at the top level
   */
  const signOffBillingMonth: ISignOffBillingMonthService = async ({
    clinicId,
    patientId,
    billingDate,
    signOffDatetime,
    jsonData,
    trackingTime,
  }) => {
    try {
      const response = await billingModuleRepository.signOffBillingMonth({
        clinic_id: clinicId,
        patient_id: patientId,
        billing_date: billingDate,
        body: {
          sign_off_information: {
            datetime: signOffDatetime,
            json_data: jsonData,
          },
          patient_id: patientId,
          clinic_id: clinicId,
          date: billingDate,
          tracking_time: {
            type: "ESign",
            start_time: trackingTime.startTime,
            end_time: trackingTime.endTime,
            total_time: trackingTime.totalTime,
            notes: trackingTime.notes,
          },
        },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  const markBillingAsBilled: IMarkBillingAsBilledService = async ({
    clinicId,
    patientId,
    billingDate,
    billedDatetime,
    note,
  }) => {
    try {
      const response = await billingModuleRepository.markBillingAsBilled({
        clinic_id: clinicId,
        patient_id: patientId,
        billing_date: billingDate,
        body: {
          billed_information: {
            text: note ?? "",
            datetime: billedDatetime,
          },
          patient_id: patientId,
          clinic_id: clinicId,
          date: billingDate,
        },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  /**
   * Create a billing Order Note (ClinicPatientCarePlan).
   * POST /clinic/{cid}/patient/care-plan/v5/{pid}
   *
   * `date` is the caller-supplied UTC "YYYY-MM-DD HH:mm:ss" timestamp.
   * Backend generates the signed PDF server-side from the HTML `message`.
   */
  const createCarePlan: ICreateCarePlanService = async ({
    clinicId,
    patientId,
    message,
    date,
    trackingTime,
  }) => {
    try {
      const response = await billingModuleRepository.createCarePlan({
        clinic_id: clinicId,
        patient_id: patientId,
        body: {
          message,
          patient_id: patientId,
          clinic_id: clinicId,
          date,
          type: "careplan",
          tracking_time: {
            is_billable: true,
            notes: "Order Notes Created",
            start_time: trackingTime.startTime,
            end_time: trackingTime.endTime,
            total_time: trackingTime.totalTime,
          },
        },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  /**
   * Edit an existing billing Order Note.
   * PUT /clinic/{cid}/patient/care-plan/v2/{pid}/{care_plan_id}
   *
   * Backend returns [carePlan, user] array — we surface it as-is.
   */
  const updateCarePlan: IUpdateCarePlanService = async ({
    clinicId,
    patientId,
    carePlanId,
    message,
    // `date` is kept in the service-contract type for parity with
    // `createCarePlan`, but the legacy update endpoint doesn't accept
    // a body `date` (the care plan retains its original creation date,
    // only the message/tracking_time fields update).  Omitted from
    // the destructure so ESLint's no-unused-vars stays clean.
    trackingTime,
  }) => {
    try {
      const response = await billingModuleRepository.updateCarePlan({
        clinic_id: clinicId,
        patient_id: patientId,
        care_plan_id: carePlanId,
        body: {
          message,
          care_plan_id: carePlanId,
          patient_id: patientId,
          clinic_id: clinicId,
          status: 2,
          tracking_time: {
            is_billable: true,
            notes: "Order Notes Created",
            start_time: trackingTime.startTime,
            end_time: trackingTime.endTime,
            total_time: trackingTime.totalTime,
          },
        },
      });
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: toErr(error) };
    }
  };

  return {
    getBillingMonthly,
    getBillingPatientMonthly,
    getSignOffAssessment,
    updateBillingStatus,
    bulkUpdateBillingStatus,
    updateSignOffAssessment,
    getBillingChargesByPatientMonth,
    signOffBillingMonth,
    markBillingAsBilled,
    createCarePlan,
    updateCarePlan,
  };
};
